import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  IconButton,
  Collapse,
  Box as ChakraBox
} from "@chakra-ui/react";
import { 
  FiDollarSign, 
  FiPlus, 
  FiTrendingUp, 
  FiTrendingDown,
  FiCreditCard,
  FiRefreshCw,
  FiEye,
  FiCheckCircle,
  FiAlertTriangle,
  FiInfo,
  FiChevronDown,
  FiChevronUp,
  FiChevronRight
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { useAccessControl } from "../../../hooks/useAccessControl";
import { useGetPlanInfo } from "../../../services/planService";
import { formatCurrency, getCurrencySymbol } from "../../../utils/currency";
import { 
  useGetCompanyWallet, 
  useFundWallet, 
  useConfirmFunding, 
  useVerifyAndUpdateFunding, 
  useGetFundingHistory 
} from "../../../services/payrollService";
import FundingCalculator from "./FundingCalculator";
import SettlementBreakdown from "./SettlementBreakdown";

const WalletManagement = () => {
  const { user } = useUserContext();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isVerificationSuccessOpen, onOpen: onVerificationSuccessOpen, onClose: onVerificationSuccessClose } = useDisclosure();
  const [fundingAmount, setFundingAmount] = useState(0);
  const [formattedAmount, setFormattedAmount] = useState("0");
  const [currency, setCurrency] = useState("USD");
  const [isPolling, setIsPolling] = useState(false);
  const [pollingStartTime, setPollingStartTime] = useState(null);
  const [initialBalance, setInitialBalance] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // 'idle', 'pending', 'completed', 'failed'
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [transactionReference, setTransactionReference] = useState(null);
  const pollingIntervalRef = useRef(null);
  const eventSourceRef = useRef(null);
  
  // Two-step modal state
  const [fundingStep, setFundingStep] = useState('form'); // 'form' or 'preview'
  const [fundingPreview, setFundingPreview] = useState(null); // Store fund-wallet response
  
  // Expandable rows for transaction history
  const [expandedHistoryRows, setExpandedHistoryRows] = useState(new Set());
  const [transactionHistoryPage, setTransactionHistoryPage] = useState(1);
  
  // Manual verification success data
  const [verificationSuccessData, setVerificationSuccessData] = useState(null);
  
  const toggleHistoryRow = (itemId) => {
    const newExpanded = new Set(expandedHistoryRows);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedHistoryRows(newExpanded);
  };
  
  // Get plan info for country-based currency
  const { planInfo } = useGetPlanInfo();
  
  // Set default currency based on plan country
  useEffect(() => {
    if (planInfo?.country) {
      const countryCurrencyMap = {
        'Nigeria': 'NGN',
        'Ghana': 'GHS', 
        'Kenya': 'KES',
        'South Africa': 'ZAR',
        'United Kingdom': 'GBP',
        'UK': 'GBP'
      };
      const defaultCurrency = countryCurrencyMap[planInfo.country] || 'USD';
      setCurrency(defaultCurrency);
    }
  }, [planInfo?.country]);

  // Initialize formatted amount when modal opens or fundingAmount changes
  useEffect(() => {
    if (fundingAmount > 0) {
      setFormattedAmount(fundingAmount.toLocaleString('en-US', { maximumFractionDigits: 2 }));
    } else {
      setFormattedAmount('');
    }
  }, [fundingAmount]);

  // Reset formatted amount when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFundingAmount(0);
      setFormattedAmount('0');
    }
  }, [isOpen]);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // API hooks
  const { data: walletData, isLoading: walletLoading, refetch: refetchWallet } = useGetCompanyWallet();
  const { data: fundingHistory, isLoading: historyLoading } = useGetFundingHistory({ page: transactionHistoryPage, limit: 10 });
  const fundWalletMutation = useFundWallet();
  const confirmFundingMutation = useConfirmFunding();
  const verifyFundingMutation = useVerifyAndUpdateFunding();

  // Polling logic for payment completion (aligned with backend recommendations)
  useEffect(() => {
    if (isPolling && pollingStartTime) {
      const POLLING_INTERVAL = 5000; // 5 seconds (as recommended by backend)
      const MAX_ATTEMPTS = 24; // 2 minutes (24 attempts * 5 seconds)
      
      // Start polling for payment completion (backend handles webhooks automatically)
      console.log("Starting payment monitoring with polling strategy...");
      
      let attempts = 0;
      pollingIntervalRef.current = setInterval(async () => {
        attempts++;
        
        // Check for max attempts (2 minutes)
        if (attempts >= MAX_ATTEMPTS) {
          stopPaymentMonitoring();
          toast({
            title: "Payment Timeout",
            description: "Payment verification timed out after 2 minutes. Please check your wallet balance manually.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          return;
        }
        
        try {
          // Refetch wallet data to check for balance increase
          const updatedWallet = await refetchWallet();
          const currentBalance = updatedWallet.data?.available_balance ?? updatedWallet.data?.balance ?? updatedWallet.data?.current_balance ?? 0;
          
          
          // Check if balance has increased (payment completed)
          // Use a small threshold to account for floating point precision
          const balanceIncrease = currentBalance - initialBalance;
          if (balanceIncrease > 0.01) {
            const amountAdded = balanceIncrease;
            stopPaymentMonitoring();
            setPaymentStatus('completed');
            toast({
              title: "Payment Successful!",
              description: `Your wallet has been funded with ${formatCurrency(amountAdded, planInfo?.country)}`,
              status: "success",
              duration: 8000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error polling wallet balance:", error);
        }
      }, POLLING_INTERVAL);
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [isPolling, pollingStartTime, initialBalance, refetchWallet, currency, toast, paymentStatus]);

  // Simulate webhook call to backend (automatic after payment completion)
  const simulateWebhookCall = async () => {
    try {
      if (!transactionReference) {
        console.log("No transaction reference available for webhook call");
        return;
      }
      
      console.log("Calling webhook to backend with transaction reference:", transactionReference);
      
      const webhookPayload = {
        event: "charge.completed",
        data: {
          tx_ref: transactionReference,
          amount: fundingAmount,
          currency: currency,
          status: "successful",
          customer: {
            email: "user@example.com", // You might want to get this from user context
            name: "User"
          },
          payment_type: "card",
          created_at: new Date().toISOString()
        }
      };
      
      const response = await fetch('/api/payroll/webhook/flutterwave', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });
      
      const result = await response.json();
      console.log("Webhook call result:", result);
      
      if (response.ok) {
        toast({
          title: "✅ Webhook Sent!",
          description: "Payment webhook has been sent to backend for processing.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Refresh wallet balance after webhook
        setTimeout(() => {
          refetchWallet();
        }, 2000);
      } else {
        console.error("Webhook call failed:", result);
        toast({
          title: "❌ Webhook Failed",
          description: "Failed to send webhook. You can use manual confirmation.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error calling webhook:", error);
      toast({
        title: "❌ Webhook Error",
        description: "Error sending webhook. You can use manual confirmation.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Manual payment confirmation function (using verify-and-update-funding)
  const handleManualConfirmation = async () => {
    try {
      if (!transactionReference) {
        toast({
          title: "No Transaction Reference",
          description: "No transaction reference available. Please try funding again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Validate tx_ref format
      const txRefPattern = /^WALLET-[a-f0-9]+-[\d.]+$/;
      if (!txRefPattern.test(transactionReference)) {
        toast({
          title: "Invalid Transaction Reference",
          description: "Transaction reference format is invalid. Please check and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Use verify-and-update-funding endpoint (returns full fee breakdown)
      const result = await verifyFundingMutation.mutateAsync({
        tx_ref: transactionReference
      });
      
      if (result.success) {
        // Store success data and show modal
        setVerificationSuccessData(result);
        onVerificationSuccessOpen();
        
        // Refresh wallet balance
        refetchWallet();
        setPaymentStatus('completed');
        stopPaymentMonitoring();
      }
    } catch (error) {
      console.error("Manual confirmation failed:", error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || "Failed to confirm payment. Please try again.";
      toast({
        title: "Confirmation Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };


  const startPaymentMonitoring = () => {
    setIsPolling(true);
    setPollingStartTime(Date.now());
    setInitialBalance(walletData?.available_balance ?? walletData?.balance ?? walletData?.current_balance ?? 0);
    setPaymentStatus('pending');
    setCurrentTransaction({
      amount: fundingAmount,
      currency: currency,
      timestamp: new Date().toISOString()
    });
    
  };

  const stopPaymentMonitoring = () => {
    setIsPolling(false);
    setPollingStartTime(null);
    setPaymentStatus('idle');
    setCurrentTransaction(null);
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    console.log("Payment monitoring stopped");
  };

  // Get supported currencies from utility
  const currencies = [
    { code: "USD", name: "US Dollar", symbol: getCurrencySymbol("Other") },
    { code: "NGN", name: "Nigerian Naira", symbol: getCurrencySymbol("Nigeria") },
    { code: "GHS", name: "Ghanaian Cedi", symbol: getCurrencySymbol("Ghana") },
    { code: "KES", name: "Kenyan Shilling", symbol: getCurrencySymbol("Kenya") },
    { code: "ZAR", name: "South African Rand", symbol: getCurrencySymbol("South Africa") },
    { code: "GBP", name: "British Pound", symbol: getCurrencySymbol("United Kingdom") }
  ];

  // Currency-specific validation limits
  const maxAmounts = {
    'NGN': 10000000, // 10M NGN
    'USD': 50000,    // 50K USD
    'GHS': 100000,   // 100K GHS
    'ZAR': 500000,   // 500K ZAR
    'KES': 1000000,  // 1M KES
    'GBP': 30000     // 30K GBP
  };

  // Input validation function
  const validateFundingAmount = (amount, currency) => {
    if (!amount || amount <= 0) {
      return { isValid: false, message: 'Amount must be greater than 0' };
    }
    
    if (amount > maxAmounts[currency]) {
      return { 
        isValid: false, 
        message: `Amount exceeds maximum limit of ${formatCurrency(maxAmounts[currency], planInfo?.country)} for ${currency}` 
      };
    }
    
    return { isValid: true, message: 'Valid amount' };
  };


  const handleFundWallet = async () => {
    try {
      // Validate funding amount
      const validation = validateFundingAmount(fundingAmount, currency);
      if (!validation.isValid) {
        toast({
          title: "Invalid Amount",
          description: validation.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Call API to get payment link and fee breakdown
      const result = await fundWalletMutation.mutateAsync({
        amount: fundingAmount,
        currency: currency
      });

      if (result.success) {
        if (result.payment_link) {
          // Validate payment link URL
          if (!result.payment_link || !result.payment_link.startsWith('http')) {
          toast({
              title: "Invalid Payment Link",
              description: "The payment link is invalid. Please try again.",
              status: "error",
              duration: 5000,
            isClosable: true,
          });
            return;
          }
          
          // Store preview data and show preview step
          setFundingPreview(result);
          setFundingStep('preview');
        } else if (result.virtual_account) {
          // Handle virtual account (keep existing flow)
          toast({
            title: "Virtual Account Details",
            description: `Transfer ${currency} ${fundingAmount} to account: ${result.virtual_account.account_number}. We'll monitor for your payment.`,
            status: "info",
            duration: 10000,
            isClosable: true,
          });
          startPaymentMonitoring();
          onClose();
        } else if (result.bank_details) {
          // Handle bank transfer (keep existing flow)
          toast({
            title: "Bank Transfer Details",
            description: `Transfer ${currency} ${fundingAmount} to the provided bank account. We'll monitor for your payment.`,
            status: "info",
            duration: 10000,
            isClosable: true,
          });
          startPaymentMonitoring();
        onClose();
        }
      }
    } catch (error) {
      toast({
        title: "Funding Failed",
        description: error.response?.data?.detail || "Failed to initiate wallet funding",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleProceedToPayment = () => {
    if (!fundingPreview?.payment_link) {
      toast({
        title: "Invalid Payment Link",
        description: "Payment link is missing. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Store transaction reference for payment success page
    if (fundingPreview.tx_ref) {
      sessionStorage.setItem('pending_tx_ref', fundingPreview.tx_ref);
      localStorage.setItem('pending_tx_ref_backup', fundingPreview.tx_ref); // Fallback
      setTransactionReference(fundingPreview.tx_ref);
    }

    // Start monitoring before redirect
    startPaymentMonitoring();

    // Close modal first
    onClose();
    setFundingStep('form');
    setFundingPreview(null);

    // Redirect to payment page
    setTimeout(() => {
      window.location.href = fundingPreview.payment_link;
    }, 300);
  };

  const handleBackToForm = () => {
    setFundingStep('form');
    setFundingPreview(null);
  };

  const handleConfirmFunding = async (transactionRef, amount, currency) => {
    try {
      await confirmFundingMutation.mutateAsync({
        transaction_reference: transactionRef,
        amount,
        currency
      });
      
      toast({
        title: "Funding Confirmed",
        description: "Wallet has been successfully funded",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      refetchWallet();
    } catch (error) {
      toast({
        title: "Confirmation Failed",
        description: error.response?.data?.detail || "Failed to confirm funding",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Use the centralized currency utility function
  const getCurrencySymbolForCode = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency?.symbol || currencyCode;
  };

  // Access control - check if user can view wallet
  const { canViewWallet, canManageFunding } = useAccessControl();
  
  if (!canViewWallet()) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Access Restricted!</AlertTitle>
            <AlertDescription>
              You don't have permission to access wallet management.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Action Buttons */}
      <Flex justify="flex-end" align="center">
        <HStack spacing={3}>
          <Button
            leftIcon={<FiRefreshCw />}
            variant="outline"
            onClick={() => refetchWallet()}
            size="md"
            borderRadius="lg"
            isLoading={walletLoading}
          >
            Refresh
          </Button>
          {canManageFunding() && (
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={onOpen}
            size="md"
            borderRadius="lg"
            isDisabled={isPolling}
          >
            Fund Wallet
          </Button>
          )}
          {isPolling && (
            <Button
              colorScheme="red"
              variant="outline"
              onClick={stopPaymentMonitoring}
              size="md"
              borderRadius="lg"
            >
              Stop Monitoring
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Payment Status Indicator */}
      {isPolling && (
        <Box bg={paymentStatus === 'completed' ? 'green.50' : 'blue.50'} p={4} borderRadius="lg" border="1px solid" borderColor={paymentStatus === 'completed' ? 'green.200' : 'blue.200'}>
          <HStack spacing={3} justify="space-between">
            <HStack spacing={3}>
              <Box>
                {paymentStatus === 'completed' ? (
                  <FiCheckCircle color="green" size={20} />
                ) : (
                  <FiRefreshCw className="animate-spin" color="blue" size={20} />
                )}
              </Box>
              <VStack align="start" spacing={1}>
                <Text fontWeight="600" color={paymentStatus === 'completed' ? 'green.700' : 'blue.700'}>
                  {paymentStatus === 'completed' ? 'Payment Completed!' : 'Monitoring Payment...'}
                </Text>
                <Text fontSize="sm" color={paymentStatus === 'completed' ? 'green.600' : 'blue.600'}>
                  {paymentStatus === 'completed' 
                    ? 'Your wallet has been successfully funded! Check the funding history below for detailed fee breakdown.'
                    : `Monitoring payment of ${formatCurrency(currentTransaction?.amount || 0, planInfo?.country)}. TX: ${transactionReference || 'N/A'}`
                  }
                </Text>
                {paymentStatus === 'completed' && (
                  <Text fontSize="xs" color="green.700" mt={1}>
                    💡 Payment processing fees have been deducted. View transaction details in Funding History below.
                  </Text>
                )}
                {paymentStatus === 'pending' && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    💡 After completing payment, the amount will be automatically processed. Actual fees will be shown in the transaction details.
                  </Text>
                )}
              </VStack>
            </HStack>
            {paymentStatus === 'pending' && (
              <HStack spacing={2}>
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={() => refetchWallet()}
                  leftIcon={<FiRefreshCw />}
                >
                  Check Balance
                </Button>
                <Button
                  colorScheme="orange"
                  size="sm"
                  onClick={simulateWebhookCall}
                  leftIcon={<FiCheckCircle />}
                >
                  Call Webhook
                </Button>
                {canManageFunding() && (
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={handleManualConfirmation}
                    isLoading={verifyFundingMutation.isPending}
                  leftIcon={<FiCheckCircle />}
                >
                  Confirm Payment
                </Button>
                )}
              </HStack>
            )}
          </HStack>
        </Box>
      )}

      {/* Tabs */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Wallet Balance</Tab>
          <Tab>Funding Calculator</Tab>
          <Tab>Settlement Breakdown</Tab>
        </TabList>
        
        <TabPanels>
          <TabPanel px={0}>
            <VStack spacing={6} align="stretch">

      {/* Wallet Balance */}
      {walletLoading ? (
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <Text>Loading wallet information...</Text>
        </Box>
      ) : walletData ? (
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Heading size="md" color="gray.800">
                Current Balance
              </Heading>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="ghost"
                onClick={refetchWallet}
              >
                Refresh
              </Button>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 2, lg: walletData.pending_settlement !== undefined && walletData.pending_settlement > 0 ? 4 : 3 }} spacing={6}>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <Text>Available Balance</Text>
                    <Tooltip 
                      label="Funds you can use immediately for payroll processing. This is your primary balance."
                      fontSize="sm"
                      hasArrow
                      placement="top"
                    >
                      <IconButton
                        icon={<FiInfo />}
                        aria-label="Info"
                        variant="ghost"
                        size="xs"
                        colorScheme="gray"
                      />
                    </Tooltip>
                  {isPolling && (
                      <Badge colorScheme="blue" ml={1} fontSize="xs">
                      Monitoring Payment
                    </Badge>
                  )}
                  </HStack>
                </StatLabel>
                <StatNumber color="green.500">
                  {formatCurrency(walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0, planInfo?.country)}
                </StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {isPolling ? "Waiting for payment confirmation..." : "Available for payroll"}
                </StatHelpText>
              </Stat>

              {walletData.pending_settlement !== undefined && walletData.pending_settlement > 0 && (
              <Stat>
                  <StatLabel>
                    <HStack spacing={1}>
                      <Text>Pending Settlement</Text>
                      <Tooltip 
                        label="Funds waiting for settlement (1-5 business days). These will become available automatically."
                        fontSize="sm"
                        hasArrow
                        placement="top"
                      >
                        <IconButton
                          icon={<FiInfo />}
                          aria-label="Info"
                          variant="ghost"
                          size="xs"
                          colorScheme="gray"
                        />
                      </Tooltip>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="orange.500">
                    {formatCurrency(walletData.pending_settlement, planInfo?.country)}
                  </StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    Available in 1-5 days
                  </StatHelpText>
                </Stat>
              )}

              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <Text>Total Funded</Text>
                    <Tooltip 
                      label="Total amount you have paid (before processing fees). This includes all fees paid to payment processors."
                      fontSize="sm"
                      hasArrow
                      placement="top"
                    >
                      <IconButton
                        icon={<FiInfo />}
                        aria-label="Info"
                        variant="ghost"
                        size="xs"
                        colorScheme="gray"
                      />
                    </Tooltip>
                  </HStack>
                </StatLabel>
                <StatNumber color="blue.500">
                  {formatCurrency(walletData.total_funded || 0, planInfo?.country)}
                </StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  Lifetime funding
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Total Spent</StatLabel>
                <StatNumber color="orange.500">
                  {formatCurrency(walletData.total_spent || 0, planInfo?.country)}
                </StatNumber>
                <StatHelpText>
                  <StatArrow type="decrease" />
                  Payroll payments
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <Text>Total Fees Paid</Text>
                    <Tooltip 
                      label="Lifetime total of all processing fees (transaction fees + VAT) paid on wallet funding transactions."
                      fontSize="sm"
                      hasArrow
                      placement="top"
                    >
                      <IconButton
                        icon={<FiInfo />}
                        aria-label="Info"
                        variant="ghost"
                        size="xs"
                        colorScheme="gray"
                      />
                    </Tooltip>
                  </HStack>
                </StatLabel>
                <StatNumber color="purple.500">
                  {formatCurrency(walletData.total_fees_paid || 0, planInfo?.country)}
                </StatNumber>
                <StatHelpText>
                  <StatArrow type="decrease" />
                  Lifetime fees
                </StatHelpText>
              </Stat>
            </SimpleGrid>

            {/* Status and Warnings */}
            {walletData.status && (
              <Box>
                <HStack spacing={2} mb={walletData.warnings?.length > 0 ? 3 : 0}>
                  <Text fontSize="sm" color="gray.600" fontWeight="500">
                    Status:
                  </Text>
                  <Badge 
                    colorScheme={
                      walletData.status === "sufficient" ? "green" :
                      walletData.status === "pending_settlement" ? "yellow" :
                      walletData.status === "funds_on_hold" ? "yellow" :
                      walletData.status === "insufficient" ? "red" :
                      walletData.status === "low_balance" ? "orange" : "gray"
                    }
                    fontSize="sm"
                    px={2}
                    py={1}
                  >
                    {walletData.status === "sufficient" && "✓ Sufficient"}
                    {walletData.status === "pending_settlement" && "⏳ Pending Settlement"}
                    {walletData.status === "funds_on_hold" && "⏳ Funds on Hold"}
                    {walletData.status === "insufficient" && "✗ Insufficient"}
                    {walletData.status === "low_balance" && "⚠ Low Balance"}
                  </Badge>
                  {walletData.is_active !== undefined && (
                    <Badge colorScheme={walletData.is_active ? "green" : "red"}>
                      {walletData.is_active ? "Active" : "Inactive"}
                    </Badge>
                  )}
                </HStack>
                
                {/* Warnings */}
                {walletData.warnings && walletData.warnings.length > 0 && (
                  <VStack spacing={2} align="stretch" mb={3}>
                    {walletData.warnings.map((warning, index) => (
                      <Alert 
                        key={index}
                        status={
                          walletData.status === "pending_settlement" ? "info" :
                          walletData.status === "funds_on_hold" ? "warning" :
                          walletData.status === "insufficient" ? "error" :
                          walletData.status === "low_balance" ? "warning" : "info"
                        }
                        borderRadius="md"
                        fontSize="sm"
                      >
                        <AlertIcon />
                        <AlertDescription>{warning}</AlertDescription>
                      </Alert>
                    ))}
                  </VStack>
                )}
              </Box>
            )}

            <HStack justify="space-between" pt={4} borderTop="1px solid" borderColor={borderColor}>
              {!walletData.status && (
              <Text fontSize="sm" color="gray.600">
                Wallet Status: <Badge colorScheme={walletData.is_active ? "green" : "red"}>
                  {walletData.is_active ? "Active" : "Inactive"}
                </Badge>
              </Text>
              )}
              <Text fontSize="sm" color="gray.600">
                Last Updated: {new Date(walletData.updated_at).toLocaleDateString()}
                {walletData.balance_synced !== undefined && (
                  <Badge ml={2} colorScheme={walletData.balance_synced ? "green" : "red"} fontSize="xs">
                    {walletData.balance_synced ? "Synced" : "Sync Failed"}
                  </Badge>
                )}
              </Text>
            </HStack>
          </VStack>
        </Box>
      ) : (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertTitle>Failed to load wallet data</AlertTitle>
          <AlertDescription>
            Unable to retrieve wallet information. Please try again.
          </AlertDescription>
        </Alert>
      )}

      {/* Transaction History */}
      <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <Heading size="md" color="gray.800">
            Transaction History
          </Heading>
          
          {historyLoading ? (
            <Text>Loading transaction history...</Text>
          ) : fundingHistory?.transactions?.length > 0 ? (
            <Box>
              <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th>Amount</Th>
                    <Th>Balance After</Th>
                    <Th>Transaction Reference</Th>
                    <Th>Processed By</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {fundingHistory.transactions.map((transaction) => {
                    const isExpanded = expandedHistoryRows.has(transaction.id);
                    const isFunding = transaction.type === 'FUNDING';
                    const isPayroll = transaction.type === 'PAYROLL';
                    const isSettlement = transaction.type === 'SETTLEMENT';
                    const isDirectDebit = transaction.type === 'DIRECT_DEBIT';
                    const hasFees = isFunding && transaction.details?.fees;
                    const amount = transaction.amount || 0;
                    const isNegative = amount < 0;
                    const paymentMethod = transaction.details?.payment_method || transaction.payment_method;
                    
                    return (
                      <React.Fragment key={transaction.id}>
                        <Tr 
                          cursor={(hasFees || isPayroll || isSettlement) ? "pointer" : "default"}
                          onClick={(hasFees || isPayroll || isSettlement) ? () => toggleHistoryRow(transaction.id) : undefined}
                          _hover={(hasFees || isPayroll || isSettlement) ? { bg: "gray.50" } : {}}
                        >
                          <Td>
                            {(hasFees || isPayroll || isSettlement) && (
                              <Box 
                                as="span" 
                                display="inline-flex" 
                                alignItems="center" 
                                mr={2}
                                transform={isExpanded ? "rotate(90deg)" : "rotate(0deg)"}
                                transition="transform 0.2s"
                              >
                                <FiChevronRight size={18} color="#4299E1" />
                              </Box>
                            )}
                            <VStack align="start" spacing={0} display="inline-flex" verticalAlign="top">
                        <Text fontSize="sm">
                                {new Date(transaction.timestamp).toLocaleDateString()}
                        </Text>
                              <Text fontSize="xs" color="gray.500">
                                {new Date(transaction.timestamp).toLocaleTimeString()}
                              </Text>
                            </VStack>
                      </Td>
                      <Td>
                            <VStack align="start" spacing={1}>
                              <Badge 
                                colorScheme={
                                  isFunding ? 'green' : 
                                  isPayroll ? 'blue' : 
                                  isSettlement ? 'purple' :
                                  isDirectDebit ? 'orange' : 'gray'
                                }
                                variant="subtle"
                              >
                                {transaction.type}
                              </Badge>
                              {/* Payment Method Badge for PAYROLL and DIRECT_DEBIT */}
                              {(isPayroll || isDirectDebit) && paymentMethod && (
                                <Badge 
                                  colorScheme={paymentMethod === 'direct_debit' ? 'orange' : 'blue'}
                                  variant="outline"
                                  fontSize="xs"
                                >
                                  {paymentMethod === 'direct_debit' ? '💳 Direct Debit' : '💰 Wallet'}
                                </Badge>
                              )}
                            </VStack>
                      </Td>
                      <Td>
                            <Text fontSize="sm" fontWeight="500">
                              {transaction.description || 'N/A'}
                            </Text>
                            {(isPayroll || isSettlement || isDirectDebit) && (
                              <Text fontSize="xs" color="blue.500" mt={1}>
                                {isExpanded ? "Click to collapse" : "Click to expand"}
                              </Text>
                            )}
                      </Td>
                      <Td>
                            <Text fontWeight="600" color={isNegative ? "red.600" : "green.600"}>
                              {formatCurrency(Math.abs(amount), planInfo?.country)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {isNegative ? "Debited" : "Credited"}
                        </Text>
                      </Td>
                      <Td>
                            <Text fontWeight="600" color="gray.700">
                              {formatCurrency(transaction.balance_after || 0, planInfo?.country)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {transaction.flw_ref || transaction.transaction_reference || "N/A"}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                              {transaction.processed_by || "System"}
                        </Text>
                      </Td>
                    </Tr>
                        {/* Expandable Details */}
                        {(hasFees || isPayroll || isSettlement || isDirectDebit) && (
                          <Tr>
                            <Td colSpan={8} p={0}>
                              <Collapse in={isExpanded} animateOpacity>
                                <ChakraBox 
                                  bg={isFunding ? "orange.50" : isPayroll ? "blue.50" : isDirectDebit ? "orange.50" : "purple.50"} 
                                  p={4} 
                                  borderLeft="4px solid" 
                                  borderColor={isFunding ? "orange.400" : isPayroll ? "blue.400" : isDirectDebit ? "orange.400" : "purple.400"}
                                >
                                  <VStack align="stretch" spacing={3}>
                                    {isFunding && (
                                      <>
                                        <Heading size="xs" color="orange.800">
                                          Funding Details
                                        </Heading>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          <Box>
                                            <Text fontSize="xs" color="orange.700" mb={1}>
                                              Amount Charged:
                                            </Text>
                                            <Text fontWeight="600" color="orange.800">
                                              {formatCurrency(transaction.details?.amount_charged || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                          <Box>
                                            <Text fontSize="xs" color="orange.700" mb={1}>
                                              Amount Settled:
                                            </Text>
                                            <Text fontWeight="600" color="orange.800">
                                              {formatCurrency(transaction.details?.amount_settled || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                          {transaction.previous_balance !== undefined && (
                                            <Box>
                                              <Text fontSize="xs" color="orange.700" mb={1}>
                                                Previous Balance:
                                              </Text>
                                              <Text fontWeight="600" color="orange.800">
                                                {formatCurrency(transaction.previous_balance, planInfo?.country)}
                                              </Text>
                                            </Box>
                                          )}
                                        </SimpleGrid>
                                        {hasFees && (
                                          <>
                                            <Divider borderColor="orange.300" />
                                            <Heading size="xs" color="orange.800">
                                              Fee Breakdown
                                            </Heading>
                                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                              <Box>
                                                <Text fontSize="xs" color="orange.700" mb={1}>
                                                  Transaction Fee:
                                                </Text>
                                                <Text fontWeight="600" color="orange.800">
                                                  {formatCurrency(transaction.details.fees?.transaction_fee || 0, planInfo?.country)}
                                                </Text>
                                              </Box>
                                              <Box>
                                                <Text fontSize="xs" color="orange.700" mb={1}>
                                                  VAT:
                                                </Text>
                                                <Text fontWeight="600" color="orange.800">
                                                  {formatCurrency(transaction.details.fees?.vat || 0, planInfo?.country)}
                                                </Text>
                                              </Box>
                                              <Box>
                                                <Text fontSize="xs" color="orange.700" mb={1}>
                                                  Total Fees:
                                                </Text>
                                                <Text fontWeight="700" fontSize="lg" color="orange.900">
                                                  {formatCurrency(transaction.details.fees?.total || 0, planInfo?.country)}
                                                </Text>
                                              </Box>
                                            </SimpleGrid>
                                          </>
                                        )}
                                        {(transaction.transaction_reference || transaction.flw_ref) && (
                                          <>
                                            <Divider borderColor="orange.300" />
                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                              {transaction.transaction_reference && (
                                                <Box>
                                                  <Text fontSize="xs" color="gray.600" mb={1}>
                                                    Transaction Reference:
                                                  </Text>
                                                  <Text fontFamily="mono" fontSize="sm" color="gray.800">
                                                    {transaction.transaction_reference}
                                                  </Text>
                                                </Box>
                                              )}
                                              {transaction.flw_ref && (
                                                <Box>
                                                  <Text fontSize="xs" color="gray.600" mb={1}>
                                                    Flw Reference:
                                                  </Text>
                                                  <Text fontFamily="mono" fontSize="sm" color="gray.800">
                                                    {transaction.flw_ref}
                                                  </Text>
                                                </Box>
                                              )}
                                            </SimpleGrid>
                                          </>
                                        )}
                                      </>
                                    )}
                                    
                                    {isPayroll && transaction.details && (
                                      <>
                                        <Heading size="xs" color="blue.800">
                                          Payroll Details
                                        </Heading>
                                        {paymentMethod && (
                                          <HStack mb={3}>
                                            <Text fontSize="xs" color="blue.700">
                                              Payment Method:
                                            </Text>
                                            <Badge 
                                              colorScheme={paymentMethod === 'direct_debit' ? 'orange' : 'blue'}
                                              variant="subtle"
                                            >
                                              {paymentMethod === 'direct_debit' ? '💳 Direct Debit' : '💰 Wallet Funding'}
                                            </Badge>
                                          </HStack>
                                        )}
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          {transaction.details.batch_name && (
                                            <Box>
                                              <Text fontSize="xs" color="blue.700" mb={1}>
                                                Batch Name:
                                              </Text>
                                              <Text fontWeight="600" color="blue.800">
                                                {transaction.details.batch_name}
                                              </Text>
                                            </Box>
                                          )}
                                          {transaction.details.employee_count !== undefined && (
                                            <Box>
                                              <Text fontSize="xs" color="blue.700" mb={1}>
                                                Employees:
                                              </Text>
                                              <Text fontWeight="600" color="blue.800">
                                                {transaction.details.employee_count}
                                              </Text>
                                            </Box>
                                          )}
                                          <Box>
                                            <Text fontSize="xs" color="blue.700" mb={1}>
                                              Total Amount:
                                            </Text>
                                            <Text fontWeight="600" color="blue.800">
                                              {formatCurrency(transaction.details.total_amount || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                        </SimpleGrid>
                                        <Divider borderColor="blue.300" />
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          <Box>
                                            <Text fontSize="xs" color="blue.700" mb={1}>
                                              Net Salaries:
                                            </Text>
                                            <Text fontWeight="600" color="blue.800">
                                              {formatCurrency(transaction.details.net_salaries || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                          <Box>
                                            <Text fontSize="xs" color="blue.700" mb={1}>
                                              Transfer Charges:
                                            </Text>
                                            <Text fontWeight="600" color="blue.800">
                                              {formatCurrency(transaction.details.transfer_charges || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                          <Box>
                                            <Text fontSize="xs" color="blue.700" mb={1}>
                                              Processing Fees:
                                            </Text>
                                            <Text fontWeight="600" color="blue.800">
                                              {formatCurrency(transaction.details.processing_fees || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                        </SimpleGrid>
                                        <Divider borderColor="blue.300" />
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          {transaction.details.successful_payments !== undefined && (
                                            <Box>
                                              <Text fontSize="xs" color="green.700" mb={1}>
                                                Successful Payments:
                                              </Text>
                                              <Text fontWeight="600" color="green.800">
                                                {transaction.details.successful_payments}
                                              </Text>
                                            </Box>
                                          )}
                                          {transaction.details.failed_payments !== undefined && (
                                            <Box>
                                              <Text fontSize="xs" color="red.700" mb={1}>
                                                Failed Payments:
                                              </Text>
                                              <Text fontWeight="600" color="red.800">
                                                {transaction.details.failed_payments}
                                              </Text>
                                            </Box>
                                          )}
                                        </SimpleGrid>
                                      </>
                                    )}

                                    {isDirectDebit && transaction.details && (
                                      <>
                                        <Heading size="xs" color="orange.800">
                                          Direct Debit Details
                                        </Heading>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          {transaction.details.batch_name && (
                                            <Box>
                                              <Text fontSize="xs" color="orange.700" mb={1}>
                                                Batch Name:
                                              </Text>
                                              <Text fontWeight="600" color="orange.800">
                                                {transaction.details.batch_name}
                                              </Text>
                                            </Box>
                                          )}
                                          {transaction.details.employee_count !== undefined && (
                                            <Box>
                                              <Text fontSize="xs" color="orange.700" mb={1}>
                                                Employees:
                                              </Text>
                                              <Text fontWeight="600" color="orange.800">
                                                {transaction.details.employee_count}
                                              </Text>
                                            </Box>
                                          )}
                                          <Box>
                                            <Text fontSize="xs" color="orange.700" mb={1}>
                                              Total Debited:
                                            </Text>
                                            <Text fontWeight="600" color="orange.800">
                                              {formatCurrency(transaction.details.total_debited || transaction.amount || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                        </SimpleGrid>
                                        <Divider borderColor="orange.300" />
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          <Box>
                                            <Text fontSize="xs" color="orange.700" mb={1}>
                                              Payroll Amount:
                                            </Text>
                                            <Text fontWeight="600" color="orange.800">
                                              {formatCurrency(transaction.details.payroll_amount || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                          <Box>
                                            <Text fontSize="xs" color="orange.700" mb={1}>
                                              Flutterwave Fees:
                                            </Text>
                                            <Text fontWeight="600" color="orange.800">
                                              {formatCurrency(transaction.details.flutterwave_fees || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                          <Box>
                                            <Text fontSize="xs" color="orange.700" mb={1}>
                                              Processing Fee:
                                            </Text>
                                            <Text fontWeight="600" color="orange.800">
                                              {formatCurrency(transaction.details.processing_fee || 0, planInfo?.country)}
                                            </Text>
                                          </Box>
                                        </SimpleGrid>
                                      </>
                                    )}

                                    {isSettlement && transaction.details && (
                                      <>
                                        <Heading size="xs" color="purple.800">
                                          Settlement Details
                                        </Heading>
                                        <Text fontSize="sm" color="purple.700">
                                          {JSON.stringify(transaction.details, null, 2)}
                                        </Text>
                                      </>
                                    )}
                                  </VStack>
                                </ChakraBox>
                              </Collapse>
                            </Td>
                          </Tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {fundingHistory?.total_pages > 1 && (
              <HStack justify="space-between" align="center" mt={4} pt={4} borderTop="1px solid" borderColor={borderColor}>
                <Text fontSize="sm" color="gray.600">
                  Page {transactionHistoryPage} of {fundingHistory.total_pages}
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    onClick={() => setTransactionHistoryPage(p => Math.max(1, p - 1))}
                    isDisabled={transactionHistoryPage === 1}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setTransactionHistoryPage(p => Math.min(fundingHistory.total_pages, p + 1))}
                    isDisabled={transactionHistoryPage === fundingHistory.total_pages}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Next
                  </Button>
                </HStack>
              </HStack>
            )}
            </Box>
          ) : (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <AlertDescription>
                No transaction history available yet.
              </AlertDescription>
            </Alert>
          )}
        </VStack>
      </Box>
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            <FundingCalculator />
          </TabPanel>
          
          <TabPanel px={0}>
            <SettlementBreakdown />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Fund Wallet Modal */}
      <Modal 
        isOpen={isOpen} 
        onClose={() => {
          onClose();
          setFundingStep('form');
          setFundingPreview(null);
        }} 
        size={fundingStep === 'preview' ? 'lg' : 'lg'}
        closeOnOverlayClick={fundingStep === 'form'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {fundingStep === 'preview' ? 'Review Funding Details' : 'Fund Company Wallet'}
          </ModalHeader>
          <ModalCloseButton isDisabled={fundingStep === 'preview'} />
          <ModalBody>
            {fundingStep === 'form' ? (
              // Step 1: Amount & Currency Selection
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Amount</FormLabel>
                  <HStack>
                    <Input
                      type="text"
                      value={formattedAmount}
                      onChange={(e) => {
                        // Remove all non-digit characters except decimal point
                        const rawValue = e.target.value.replace(/[^\d.]/g, '');
                        const numValue = parseFloat(rawValue) || 0;
                        
                        // Validate max amount
                        const maxAmount = maxAmounts[currency] || 1000000;
                        const finalValue = numValue > maxAmount ? maxAmount : numValue;
                        
                        setFundingAmount(finalValue);
                        // Format with thousand separators
                        if (rawValue === '' || rawValue === '.') {
                          setFormattedAmount('');
                        } else {
                          setFormattedAmount(finalValue.toLocaleString('en-US', { maximumFractionDigits: 2 }));
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure formatted value on blur
                        const numValue = parseFloat(e.target.value.replace(/[^\d.]/g, '')) || 0;
                        const finalValue = Math.max(numValue, 1); // Minimum 1
                        setFundingAmount(finalValue);
                        setFormattedAmount(finalValue.toLocaleString('en-US', { maximumFractionDigits: 2 }));
                      }}
                      placeholder="Enter amount"
                  min={1}
                  max={maxAmounts[currency] || 1000000}
                    />
                    <HStack spacing={1}>
                      <Button
                        size="sm"
                        onClick={() => {
                          const newValue = Math.min(fundingAmount + 1000, maxAmounts[currency] || 1000000);
                          setFundingAmount(newValue);
                          setFormattedAmount(newValue.toLocaleString('en-US', { maximumFractionDigits: 2 }));
                        }}
                        isDisabled={fundingAmount >= (maxAmounts[currency] || 1000000)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          const newValue = Math.max(fundingAmount - 1000, 1);
                          setFundingAmount(newValue);
                          setFormattedAmount(newValue.toLocaleString('en-US', { maximumFractionDigits: 2 }));
                        }}
                        isDisabled={fundingAmount <= 1}
                      >
                        ↓
                      </Button>
                    </HStack>
                  </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Max: {formatCurrency(maxAmounts[currency] || 1000000, planInfo?.country)}
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Currency</FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {currencies.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.name} ({curr.symbol})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <AlertDescription>
                    You'll see estimated fees and amount breakdown before proceeding to payment.
                </AlertDescription>
              </Alert>
            </VStack>
            ) : (
              // Step 2: Fee Preview
              fundingPreview && (
                <VStack spacing={4} align="stretch">
                  <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                    <Heading size="sm" color="blue.900" mb={3}>
                      Funding Summary
                    </Heading>
                    
                    <VStack spacing={3} align="stretch">
                      <HStack justify="space-between">
                        <Text fontWeight="500">Amount to Pay:</Text>
                        <Text fontWeight="700" fontSize="lg" color="blue.600">
                          {formatCurrency(fundingPreview.amount_charged || fundingAmount, planInfo?.country)}
                        </Text>
                      </HStack>

                      {fundingPreview.estimated_fees && (
                        <>
                          <Divider />
                          <Box>
                            <Text fontWeight="600" mb={2}>Estimated Fees:</Text>
                            <VStack align="stretch" spacing={1} pl={4}>
                              <HStack justify="space-between">
                                <Text fontSize="sm">Transaction Fee:</Text>
                                <Text fontSize="sm" fontWeight="500">
                                  {formatCurrency(fundingPreview.estimated_fees.transaction_fee || 0, planInfo?.country)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between">
                                <Text fontSize="sm">VAT:</Text>
                                <Text fontSize="sm" fontWeight="500">
                                  {formatCurrency(fundingPreview.estimated_fees.vat || 0, planInfo?.country)}
                                </Text>
                              </HStack>
                              <Divider />
                              <HStack justify="space-between">
                                <Text fontWeight="600">Total Fees:</Text>
                                <Text fontWeight="700" color="orange.600">
                                  {formatCurrency(fundingPreview.estimated_fees.total || 0, planInfo?.country)}
                                  {fundingPreview.estimated_fees.fee_percentage && (
                                    <Text as="span" fontSize="xs" ml={1} color="gray.600">
                                      ({fundingPreview.estimated_fees.fee_percentage}%)
                                    </Text>
                                  )}
                                </Text>
                              </HStack>
                            </VStack>
                          </Box>
                        </>
                      )}

                      <Divider />
                      
                      <Box bg="green.50" p={3} borderRadius="md" border="1px solid" borderColor="green.200">
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="600" color="green.800">Amount to Wallet:</Text>
                          <Text fontWeight="700" fontSize="lg" color="green.700">
                            {formatCurrency(fundingPreview.estimated_settled_amount || 0, planInfo?.country)}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="green.700">
                          This is the amount that will be added to your wallet after fees
                        </Text>
                      </Box>


                      {/* Instant Availability Indicator */}
                      {fundingPreview.instant_availability !== undefined && (
                        <>
                          <Divider />
                          <Alert 
                            status={fundingPreview.instant_availability ? "success" : "info"} 
                            borderRadius="md"
                            fontSize="sm"
                          >
                            <AlertIcon />
                            <AlertDescription>
                              {fundingPreview.instant_availability 
                                ? "✅ Funds will be available immediately after payment! You can process payroll right away."
                                : "⏳ Funds will be pending settlement (1-5 days) after payment. You'll be notified when available."}
                            </AlertDescription>
                          </Alert>
                        </>
                      )}


                      {fundingPreview.fee_disclaimer && (
                        <Alert status="warning" borderRadius="lg" mt={2}>
                          <AlertIcon />
                          <AlertDescription fontSize="xs">
                            {fundingPreview.fee_disclaimer}
                          </AlertDescription>
                        </Alert>
                      )}
                    </VStack>
                  </Box>
                </VStack>
              )
            )}
          </ModalBody>
          <ModalFooter>
            {fundingStep === 'form' ? (
              <>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleFundWallet}
              isLoading={fundWalletMutation.isPending}
            >
                  Continue to Preview
            </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={handleBackToForm}>
                  Back
                </Button>
                <Button 
                  colorScheme="green" 
                  onClick={handleProceedToPayment}
                  isLoading={fundWalletMutation.isPending}
                  leftIcon={<FiCreditCard />}
                >
                  Proceed to Payment
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Manual Verification Success Modal */}
      <Modal 
        isOpen={isVerificationSuccessOpen} 
        onClose={onVerificationSuccessClose} 
        size="lg"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <FiCheckCircle color="green" />
              <Text>Payment Verified Successfully</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {verificationSuccessData && (
              <VStack spacing={4} align="stretch">
                <Alert status="success" borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription>
                    {verificationSuccessData.message || "Payment has been verified and wallet balance updated."}
                  </AlertDescription>
                </Alert>

                {/* Amount Charged */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Amount Paid
                  </Text>
                  <Text fontSize="xl" fontWeight="700" color="blue.600">
                    {formatCurrency(verificationSuccessData.amount_charged || 0, planInfo?.country)}
                  </Text>
                </Box>

                {/* Fees Breakdown */}
                {verificationSuccessData.fees && (
                  <Box bg="orange.50" p={4} borderRadius="lg" border="1px solid" borderColor="orange.200">
                    <Heading size="sm" color="orange.800" mb={3}>
                      Actual Fees (from Flutterwave)
                    </Heading>
                    <VStack spacing={2} align="stretch" pl={2}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="orange.700">Transaction Fee:</Text>
                        <Text fontSize="sm" fontWeight="600" color="orange.800">
                          {formatCurrency(verificationSuccessData.fees.transaction_fee || 0, planInfo?.country)}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="orange.700">VAT:</Text>
                        <Text fontSize="sm" fontWeight="600" color="orange.800">
                          {formatCurrency(verificationSuccessData.fees.vat || 0, planInfo?.country)}
                        </Text>
                      </HStack>
                      <Divider borderColor="orange.300" />
                      <HStack justify="space-between">
                        <Text fontWeight="700" color="orange.800">Total Fees:</Text>
                        <Text fontWeight="700" fontSize="lg" color="orange.900">
                          {formatCurrency(verificationSuccessData.fees.total || 0, planInfo?.country)}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* Amount Settled */}
                <Box bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
                  <HStack justify="space-between">
                    <Text fontWeight="600" color="green.800" fontSize="lg">
                      Amount Added to Wallet:
                    </Text>
                    <Text fontWeight="700" fontSize="2xl" color="green.700">
                      {formatCurrency(verificationSuccessData.amount_settled || 0, planInfo?.country)}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="green.700" mt={1}>
                    This is the actual amount added after fees
                  </Text>
                </Box>

                {/* Instant Availability Alert */}
                {verificationSuccessData.instant_availability !== undefined && (
                  <Alert 
                    status={verificationSuccessData.instant_availability ? "success" : "info"} 
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    <AlertDescription>
                      {verificationSuccessData.instant_availability 
                        ? "✅ Funds are available immediately! You can process payroll now."
                        : "⏳ Funds are pending settlement (1-5 days). You'll be notified when available."}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Available Balance and Pending Settlement */}
                {(verificationSuccessData.available_balance !== undefined || verificationSuccessData.pending_settlement !== undefined) && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {verificationSuccessData.available_balance !== undefined && (
                      <Box bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.200">
                        <Text fontSize="sm" color="blue.700" mb={1}>
                          Available Balance:
                        </Text>
                        <Text fontSize="lg" fontWeight="700" color="blue.800">
                          {formatCurrency(verificationSuccessData.available_balance, planInfo?.country)}
                        </Text>
                        <Text fontSize="xs" color="blue.600" mt={1}>
                          Can use immediately
                        </Text>
                      </Box>
                    )}
                    {verificationSuccessData.pending_settlement !== undefined && verificationSuccessData.pending_settlement > 0 && (
                      <Box bg="orange.50" p={3} borderRadius="md" border="1px solid" borderColor="orange.200">
                        <Text fontSize="sm" color="orange.700" mb={1}>
                          Pending Settlement:
                        </Text>
                        <Text fontSize="lg" fontWeight="700" color="orange.800">
                          {formatCurrency(verificationSuccessData.pending_settlement, planInfo?.country)}
                        </Text>
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          Available in 1-5 days
                        </Text>
                      </Box>
                    )}
                  </SimpleGrid>
                )}


                {/* Transaction Reference */}
                {verificationSuccessData.transaction_reference && (
                  <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor={borderColor}>
                    <Text fontSize="xs" color="gray.600" mb={1}>
                      Transaction Reference:
                    </Text>
                    <Text fontFamily="mono" fontSize="sm" color="gray.800">
                      {verificationSuccessData.transaction_reference}
                    </Text>
                    {verificationSuccessData.flw_ref && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Flutterwave Ref: {verificationSuccessData.flw_ref}
                      </Text>
                    )}
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={onVerificationSuccessClose} size="lg">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default WalletManagement;
